import { PersonListItemType, useDeletePerson } from '@api';
import { useNotification } from '@common';
import { modals } from '@mantine/modals';
import { useRouter } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

type UsePersonsTableActionsReturnType = {
  handleEditPerson: (person: PersonListItemType) => void;
  handleDeletePerson: (person: PersonListItemType) => void;
};

const TRANS_KEY = 'NOTIFICATIONS.DELETE_PERSON';

export const usePersonsTableActions = (): UsePersonsTableActionsReturnType => {
  const { t } = useTranslation('persons');
  const router = useRouter();
  const { showSuccess, showError } = useNotification();

  const showSuccessNotification = () =>
    showSuccess({
      title: `${TRANS_KEY}.SUCCESS.TITLE`,
      message: `${TRANS_KEY}.SUCCESS.MESSAGE`,
    });

  const onError = (error: AxiosError) => {
    showError({
      title: `${TRANS_KEY}.ERROR.TITLE`,
      message: `${TRANS_KEY}.ERROR.MESSAGE`,
      error,
    });
  };

  const { mutate: deletePerson } = useDeletePerson({
    mutation: {
      onSuccess: () => {
        showSuccessNotification();
      },
      onError,
    },
  });

  const handleEditPerson = (person: PersonListItemType) => {
    router.navigate({
      to: '/persons/$id',
      params: { id: person.id },
    });
  };

  const handleDeletePerson = (person: PersonListItemType) => {
    modals.openConfirmModal({
      title: t('TABLE.ACTIONS.DELETE_CONFIRMATION_TITLE'),
      labels: {
        confirm: t('TABLE.ACTIONS.DELETE_CONFIRM_BUTTON'),
        cancel: t('CANCEL_BUTTON', { ns: 'common' }),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: () => deletePerson({ id: person.id }),
    });
  };

  return { handleEditPerson, handleDeletePerson };
};
